import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Layers } from 'lucide-react';
import ProductCard from './ProductCard';
import { productsAPI, cartAPI } from '../services/api';
import { vehicleStore, useSelectedVehicle } from '../services/vehicleStore';
import { useT } from '../services/i18n';

// Same-category parts from other brands fitting the same vehicles; restricted to the customer's vehicle when one is selected
const SimilarProducts = ({ productId, categoryName, items: preloaded }) => {
  const { t } = useT();
  const vehicle = useSelectedVehicle();
  const [fetched, setFetched] = useState([]);
  const [adding, setAdding] = useState(null);
  const scroller = useRef(null);
  const params = vehicleStore.toParams(vehicle);
  const items = preloaded || fetched;

  useEffect(() => {
    if (preloaded) return undefined;
    let cancelled = false;
    setFetched([]);
    productsAPI.getSimilar(productId, 8, vehicleStore.toParams(vehicle)).then((list) => {
      if (!cancelled) setFetched(list);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [productId, vehicle, preloaded]);

  const addToCart = async (id) => {
    setAdding(id);
    try {
      await cartAPI.addItem(id, 1);
      window.dispatchEvent(new Event('cart-updated'));
    } finally {
      setAdding(null);
    }
  };

  const scroll = (dir) => scroller.current?.scrollBy({ left: dir * 600, behavior: 'smooth' });

  if (!items.length) return null;

  return (
    <section id="similar-products" className="mt-8 scroll-mt-24" data-testid="similar-products">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Layers size={20} className="text-orange-500" /> {t('similar.title')}
          <span className="text-sm font-normal text-gray-500 hidden sm:inline" data-testid="similar-subtitle">{params ? t('similar.subVehicle', { vehicle: `${vehicle.brand} ${vehicle.model} ${vehicle.engine?.name || ''}`.trim() }) : t('similar.sub', { category: categoryName })}</span>
        </h2>
        <div className="hidden md:flex gap-2">
          <button type="button" data-testid="similar-prev" onClick={() => scroll(-1)} className="p-2 rounded-full border border-gray-200 bg-white hover:border-orange-400 hover:text-orange-600 transition-colors"><ChevronLeft size={18} /></button>
          <button type="button" data-testid="similar-next" onClick={() => scroll(1)} className="p-2 rounded-full border border-gray-200 bg-white hover:border-orange-400 hover:text-orange-600 transition-colors"><ChevronRight size={18} /></button>
        </div>
      </div>
      <div ref={scroller} className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin" data-testid="similar-products-list">
        {items.map(p => (
          <div key={p.id} className="min-w-[260px] max-w-[260px] snap-start" data-testid={`similar-item-${p.id}`}>
            <ProductCard product={p} compatible={!!params} onAddToCart={addToCart} isAdding={adding === p.id} />
          </div>
        ))}
      </div>
    </section>
  );
};

export default SimilarProducts;
